<script src="../../store/modules/blobs.js"></script>
<template>
  <v-card>
    <v-form
        ref="blob-form"
        lazy-validation
    >
      <v-card-title>
        {{ editableBlob.name }}
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12">
            <v-switch
                v-model="editableBlob.is_public"
                :label="$t('form.general.public.label')"
                color="success"
            ></v-switch>
          </v-col>

          <v-col cols="6">
            <v-select
                v-model="editableBlob.country"
                :label="$t('form.country.label')"
                :items="countries"
                item-text="name"
                item-value="iso"
                required
                :error-messages="countryErrors"
                @blur="$v.editableBlob.country.$touch()"
            ></v-select>
          </v-col>

          <v-col cols="6">
            <v-select
                v-model="editableBlob.locale"
                :items="nativeLocals"
                item-text="native"
                item-value="iso"
            ></v-select>
          </v-col>

          <v-col cols="12">
            <v-text-field
                v-model="editableBlob.name"
                :label="$t('form.general.name.label')"
                :error-messages="nameErrors"
                :counter="20"
                required
                @blur="$v.editableBlob.name.$touch()"
            ></v-text-field>
          </v-col>

          <v-col cols="12">
            <v-text-field
                v-model="editableBlob.link"
                :label="$t('form.general.link.label')"
                :error-messages="linkErrors"
                required
                @blur="$v.editableBlob.link.$touch()"
            ></v-text-field>
          </v-col>

          <v-col cols="12">
            <v-combobox
                v-model="editableBlob.tags"
                :items="blobSuggestionTags"
                lavel="tags"
                chips
                deletable-chips
                append-icon
                label="tags"
                multiple
            >
            </v-combobox>
          </v-col>

          <v-col cols="12">
            <v-file-input
                v-model="image"
                :rules="imageRules"
                accept="image/*"
                :label="$t('form.general.image.label')"
                show-size
                prepend-icon="mdi-camera"
            ></v-file-input>
            <v-img
                v-if="editableBlob.image"
                :src="editableBlob.image"
                max-width="300"
            />
          </v-col>

          <v-col cols="12">
            <v-textarea
                v-model="editableBlob.description"
                :label="$t('form.general.description.label')"
                :error-messages="descriptionErrors"
                required
                @blur="$v.editableBlob.description.$touch()"
            ></v-textarea>
          </v-col>

          <v-col cols="6">
            <v-select
                v-model="editableBlob.quadrant"
                :items="quadrants"
                :label="$t('form.blob.quadrants.label')"
            ></v-select>
          </v-col>

          <v-col cols="6">
            <v-select
                v-model="editableBlob.sector"
                :items="sectors"
                :label="$t('form.blob.sectors.label')"
            ></v-select>
          </v-col>

          <v-col cols="12">
            <v-slider
                v-model="editableBlob.sector_progress"
                :label="$t('form.blob.sector_progress.label')"
                step="1"
                max="10"
                thumb-label="always"
                ticks
            ></v-slider>
          </v-col>

        </v-row>
      </v-card-text>
      <v-card-actions>
        <v-btn
            text
            @click="close"
        >
          {{ $t('controls.cancel') }}
        </v-btn>

        <v-spacer></v-spacer>

        <v-btn icon right color="error" v-if="!isNew" @click="openDeleteDialog">
          <v-icon>mdi-delete</v-icon>
        </v-btn>

        <v-btn
            dark
            @click="save"
        >
                   <span v-if="isNew">
                        {{ $t('controls.create') }}
                    </span>
          <span v-else>
                        {{ $t('controls.save') }}
                    </span>
        </v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>
import { mapGetters } from 'vuex'
import { validationMixin } from 'vuelidate'
import { maxLength, minLength, required, url } from 'vuelidate/lib/validators'
import { localisation } from '@/settings/settings'

export default {
  name: 'BlobSuggestionForm',
  mixins: [validationMixin],

  validations: {
    editableBlob: {
      country: {
        required
      },
      name: {
        required,
        minLength: minLength(4),
        maxLength: maxLength(20)
      },
      link: {
        required,
        url
      },
      description: {
        required,
        minLength: minLength(10),
      }
    }
  },
  props: {
    blob: {
      type: Object,
      required: true
    },
    cancel: {
      type: Boolean,
      default: false
    }
  },

  data: function () {
    return {
      ...localisation,
      blobBlueprint: {
        is_public: false,
        country: 'CH',
        locale: '',
        name: '',
        description: '',
        quadrant: 0,
        sector: 3,
        sector_progress: 0,
        tags: [],
        image: null,
        link: 'https://'
      },
      image: null,
      imageRules: [
        value => !value || value.size < 500000 || this.$t('form.general.image.file_size'),
      ],
      editableBlob: {}
    }
  },

  computed: {
    ...mapGetters({
      countries: 'countries/getCountries',
      blobSuggestionTags: 'tags/blobSuggestionTags'
    }),

    countryErrors () {
      const errors = []
      if (!this.$v.editableBlob.country.$dirty) return errors
      !this.$v.editableBlob.country.required && errors.push(this.$t('form.country.required'))
      return errors
    },

    nameErrors () {
      const errors = []
      if (!this.$v.editableBlob.name.$dirty) return errors
      !this.$v.editableBlob.name.required && errors.push(this.$t('form.radar.name.required'))
      !this.$v.editableBlob.name.minLength && errors.push(this.$t('form.radar.name.min-length'))
      !this.$v.editableBlob.name.maxLength && errors.push(this.$t('form.radar.name.max-length'))
      return errors
    },

    linkErrors () {
      const errors = []
      if (!this.$v.editableBlob.link.$dirty) return errors
      !this.$v.editableBlob.link.required && errors.push(this.$t('form.radar.link.required'))
      !this.$v.editableBlob.link.url && errors.push(this.$t('form.radar.link.url'))
      return errors
    },

    descriptionErrors () {
      const errors = []
      if (!this.$v.editableBlob.description.$dirty) return errors
      !this.$v.editableBlob.description.required && errors.push(this.$t('form.radar.description.required'))
      !this.$v.editableBlob.description.minLength && errors.push(this.$t('form.radar.description.min-length'))
      return errors
    },

    quadrants () {
      return [
        { text: '1', value: 0 },
        { text: '2', value: 1 },
        { text: '3', value: 2 },
        { text: '4', value: 3 },
      ]
    },
    sectors () {
      return [
        { text: '4', value: 3 },
        { text: '3', value: 2 },
        { text: '2', value: 1 },
        { text: '1', value: 0 },
      ]
    },

    nativeLocals () {
      let locals = []

      for (let i in this.validLocals) {
        locals.push({ iso: this.validLocals[i], native: this.$t('language.' + this.validLocals[i]) })
      }

      return locals
    },

    isNew () {
      return this.editableBlob.id === undefined
    }
  },
  mounted () {
    this.$store.dispatch('tags/loadBlobSuggestionTags');
    this.blobBlueprint.locale = this.defaultLocale
    this.editableBlob = Object.assign(this.blobBlueprint, this.blob)
  },
  methods: {
    save () {
      this.$v.$touch()

      if (!this.$v.$invalid) {
        let dispatchMethod = this.isNew ? 'admin/createBlobSuggestion' : 'admin/updateBlobSuggestion'

        let formData = new FormData()

        formData.append('id', this.editableBlob.id);
        formData.append('is_public', this.editableBlob.is_public);
        formData.append('locale', this.editableBlob.locale);
        formData.append('country', this.editableBlob.country);
        formData.append('name', this.editableBlob.name);
        formData.append('link', this.editableBlob.link);
        formData.append('description', this.editableBlob.description);
        formData.append('quadrant', this.editableBlob.quadrant);
        formData.append('sector', this.editableBlob.sector);
        formData.append('sector_progress', this.editableBlob.sector_progress);
        formData.append('tags', this.editableBlob.tags);

        if(this.image)
          formData.append('image', this.image);

        // form method spoofing since file upload wont work with put
        // https://laravel.com/docs/5.3/routing#form-method-spoofing
        formData.append('_method', 'put')

        this.$store.dispatch(dispatchMethod, formData)
            .then((response) => {
              if (this.isNew) {
                // reset form
                this.editableBlob = {}
                this.$refs['blob-form'].reset()
                this.$v.$reset()

                this.$store.commit('notification/newNotification', {
                  text: this.$t('form.blob.created')
                })
              } else {
                this.$store.commit('notification/newNotification', {
                  text: this.$t('form.blob.updated')
                })
              }

              this.editableBlob = {}
              this.$emit('blobIsProcessed')
            })
      }
    },

    close () {
      this.$emit('blobIsCanceled')
      this.editableBlob = {}
      this.$refs['blob-form'].reset()
      this.$v.$reset()
    },

    openDeleteDialog () {
      const deletedText = this.$t('form.blob.removed')

      this.$store.commit('prompt/newPrompt', {
        title: this.$t('dialog.delete.title') + ' ' + this.editableBlob.name,
        text: this.$t('dialog.delete.blob.text'),
        execCallback: function () {

          this.$store.dispatch('admin/removeBlobSuggestion', this.editableBlob)
              .then(() => {
                // close form
                this.$emit('blobIsProcessed')
                // go back to quadrant view
                this.$emit('openQuadrant', this.editableBlob.quadrant)

                // trigger notification
                this.$store.commit('notification/newNotification', {
                  text: deletedText
                })
              })
        }.bind(this)
      })
    }
  },
}
</script>
